import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { FeedbackQuestionHandler, FeedbackSectionInfo } from '../utils';
import { useAllServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import { SectionServiceRender } from '../../../ReportBuilder/Components/ImpactSummary/SectionService';
import { ServiceGroup } from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { FeedbackQuestion, FeedbackQuestionsById } from '../api';
import { useAllReportBeneficiariesFromReportWizard } from 'shared/apollo/pageHooks/useAllReportBeneficiariesFromReportWizard';

const StyledDiv = styled.div``;

type BeneficiaryTags = {
  dbId: number;
  name: string;
}[];

type Options = ServiceGroup[] | ServiceGroup['services'] | BeneficiaryTags;

const getOptions = (
  services: ServiceGroup[],
  question: FeedbackQuestion,
  questions: FeedbackQuestionsById,
  beneficiaryTags: BeneficiaryTags,
): Options => {
  const mainService = questions[40002]?.answers[0]?.text;
  switch (question.dbId) {
    case 40003: {
      const result = services.find(x => x.name === mainService);
      if (!result) break;
      return result.services;
    }

    case 40004:
      return beneficiaryTags;

    case 40022:
      return question.enumResponses.map((x, i) => ({
        dbId: i,
        name: x.response,
      }));
  }
  return services;
};

const Service = React.forwardRef<FeedbackQuestionHandler, FeedbackSectionInfo>(
  (
    {
      reportId,
      questionId,
      answer,
      questionText,
      question,
      questions,
      guidanceText,
      loadingAnswers,
    },
    forwardRef,
  ) => {
    const [text, setText] = useState<string>(answer);
    const { data: serviceCollections } = useAllServiceCollections({ reportId });
    const {
      data: beneficiaryTags,
    } = useAllReportBeneficiariesFromReportWizard();
    const [options, setOptions] = useState<Options>(
      serviceCollections && beneficiaryTags
        ? getOptions(serviceCollections, question, questions, beneficiaryTags)
        : [],
    );

    useEffect(() => {
      setText(answer);
    }, [answer]);

    useEffect(() => {
      if (!serviceCollections || !beneficiaryTags) return;
      setOptions(
        getOptions(serviceCollections, question, questions, beneficiaryTags),
      );
    }, [serviceCollections, beneficiaryTags, question]);

    const handleChange = useCallback(
      (
        questionId: number,
        value: string,
        forcedValue?: number | null | undefined,
      ) => {
        if (!value) return;
        setText(value);
      },
      [setText],
    );

    useImperativeHandle(forwardRef, () => ({
      reportId,
      questionId,
      answer: () => text,
      errorText: () =>
        text && text.length > 0
          ? undefined
          : 'Please select one of the options',
    }));

    if (
      loadingAnswers ||
      !serviceCollections ||
      serviceCollections.length === 0 ||
      !beneficiaryTags
    ) {
      return <TrueImpactLoading />;
    }

    return (
      <StyledDiv>
        <TrueImpactText variant="heading-subheading" className="question-text">
          {questionText}
        </TrueImpactText>
        <SectionServiceRender
          answers={[]}
          data={options}
          value={text}
          questionId={questionId}
          onChange={handleChange}
          guidanceText={guidanceText}
        />
      </StyledDiv>
    );
  },
);

export default Service;
